import Config from '../config/config';

export type CertStatus = 'pending' | 'approved' | 'denied' | 'active' | 'revoked' | 'expired';
export type CertKind = 'mtls-client' | 'roles-anywhere';

export interface CertRow {
    serial: string;
    cn: string;
    san?: string[];
    status: CertStatus;
    kind: CertKind;
    source?: 'legacy' | 'portal';
    requested_by: string;
    requested_by_email?: string;
    requested_at: string;
    approved_at?: string;
    approved_by?: string;
    issued_at?: string;
    expires_at?: string;
    revoked_at?: string;
    revoked_reason?: string;
    fingerprint_sha256?: string;
}

export interface RequestCertInput {
    cn: string;
    san?: string[];
    kind?: CertKind;
    p12_password: string;
    validity_days?: number;
}

const base = () => Config.api.apiUrl.replace(/\/$/, '');

async function call<T>(path: string, init: RequestInit & { token: string }): Promise<T> {
    const { token, ...rest } = init;
    const res = await fetch(`${base()}${path}`, {
        ...rest,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...(rest.headers ?? {}),
        },
    });
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
    }
    return (await res.json()) as T;
}

export const certApi = {
    list: (token: string, kind?: CertKind) =>
        call<{ certs: CertRow[] }>(
            kind ? `/certs?kind=${kind}` : '/certs',
            { token, method: 'GET' },
        ),
    request: (token: string, input: RequestCertInput) =>
        call<{ serial: string; status: 'pending' }>(
            '/certs',
            { token, method: 'POST', body: JSON.stringify(input) },
        ),
    revoke: (token: string, serial: string, reason?: string) =>
        call<{ serial: string; status: 'revoked' }>(
            `/certs/${encodeURIComponent(serial)}/revoke`,
            { token, method: 'POST', body: JSON.stringify({ reason }) },
        ),
};
